import { Modal, Select, Textarea, TextInput } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount, DateTime, EMPTY_CAN } from './BunkeringPanel.js';
import classes from './Ledger.module.css';
import {
  DecisionModal,
  issuesOf,
  joinLocal,
  Notices,
  newKey,
  orNull,
  StatusBadge,
  StatusBanner,
  splitLocal,
  useLedgerError,
  WorkflowButtons,
  workflowActions,
} from './ledger-ui.js';
import type { FuelSource } from './source.js';
import type { FuelContextData, FuelRecordDetail, FuelTank } from './types.js';

const METHODS = ['sounding', 'ullage', 'sensor', 'bdn', 'other'] as const;

interface Values {
  fuelTypeId: string | null;
  day: string | null;
  time: string | null;
  method: string | null;
  volumeM3: string;
  density15: string;
  massMt: string;
  basis: string;
  remarks: string;
}

const EMPTY: Values = {
  fuelTypeId: null,
  day: null,
  time: null,
  method: 'sounding',
  volumeM3: '',
  density15: '',
  massMt: '',
  basis: '',
  remarks: '',
};

function valuesOf(input: Record<string, unknown>): Values {
  const at = splitLocal(input.measuredLocal);
  const s = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    fuelTypeId: typeof input.fuelTypeId === 'string' ? input.fuelTypeId : null,
    day: at.day,
    time: at.time,
    method: typeof input.method === 'string' ? input.method : null,
    volumeM3: s(input.volumeM3),
    density15: s(input.density15),
    massMt: s(input.massMt),
    basis: s(input.basis),
    remarks: s(input.remarks),
  };
}

/**
 * Açılış bakiyesi (ADR 0117 §13; ortak belgesi §6 "Açılış bakiyesi"): tank başına tarih saat,
 * ölçüm, yoğunluk, MT ve dayanak; onaylanınca ROB defterine `opening` hareketi. Tanklar ekranının
 * "Açılış Bakiyesi" düğmesi açar (ekran 02). Onaylı açılış Düzelt ile değişir (ters + yeni).
 */
export function OpeningBalanceModal({
  source,
  tank,
  vesselId,
  opened,
  onClose,
}: {
  source: FuelSource;
  tank: FuelTank | null;
  vesselId: string | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useLedgerError();
  const editor = source.ledgerEditor;
  const [context, setContext] = useState<FuelContextData | null>(null);
  const [detail, setDetail] = useState<FuelRecordDetail | null>(null);
  const [values, setValues] = useState<Values>(EMPTY);
  const [key, setKey] = useState(newKey);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [modal, setModal] = useState<'approve' | 'return' | 'reverse' | null>(null);

  const load = useCallback(
    async (select?: string) => {
      if (!tank) return;
      const [ctx, list] = await Promise.all([
        source.context(vesselId),
        source.records(vesselId, 'opening'),
      ]);
      setContext(ctx);
      // Tankın canlı açılışı: düzeltilmemiş ve ters kaydedilmemiş en yenisi
      const own = list.items.filter((r) => r.tankIds.includes(tank.id));
      const current =
        (select ? own.find((r) => r.id === select) : undefined) ??
        own.find((r) => r.status !== 'corrected' && r.status !== 'reversed') ??
        null;
      const next = current ? await source.record(vesselId, 'opening', current.id) : null;
      setDetail(next);
      setValues(
        next ? valuesOf(next.input) : { ...EMPTY, fuelTypeId: tank.allowedFuelTypeIds[0] ?? null },
      );
      setKey(newKey());
    },
    [source, vesselId, tank],
  );

  useEffect(() => {
    if (!opened) return;
    setError(null);
    setDone(null);
    setIssues({});
    load().catch((failure) => setError(errorText(failure)));
  }, [opened, load, errorText]);

  const status = detail?.status ?? 'new';
  const editable =
    editor !== undefined && (status === 'new' || status === 'draft' || status === 'returned');
  const set = <K extends keyof Values>(k: K, v: Values[K]) => setValues((p) => ({ ...p, [k]: v }));
  const issue = (path: string) =>
    issues[path] ? t(`fuel.fieldErrors.${issues[path]}`, { defaultValue: issues[path] }) : null;
  const input = () => ({
    tankId: tank?.id,
    fuelTypeId: values.fuelTypeId,
    measuredLocal: joinLocal(values.day, values.time),
    method: values.method,
    volumeM3: orNull(values.volumeM3),
    density15: orNull(values.density15),
    massMt: orNull(values.massMt),
    basis: orNull(values.basis),
    remarks: orNull(values.remarks),
  });

  const persist = async () => {
    if (!editor || !tank) throw new Error('FORBIDDEN');
    if (!detail) {
      const saved = await editor.save('opening', {
        idempotencyKey: key,
        tankId: tank.id,
        input: input(),
      });
      // Yeni kayıt hemen seçilir: gönderim düşerse sonraki deneme yeni kayıt açmaz
      await load(saved.id);
      return saved.id;
    }
    await editor.save('opening', { id: detail.id, rowVersion: detail.rowVersion, input: input() });
    return detail.id;
  };

  const act = async (action: string) => {
    if (action === 'approve' || action === 'return' || action === 'reverse') {
      setModal(action);
      return;
    }
    setBusy(action);
    setError(null);
    setDone(null);
    setIssues({});
    try {
      if (action === 'save') {
        const id = await persist();
        await load(id);
        setDone(t('fuel.records.saved'));
      } else if (action === 'submit') {
        const id = await persist();
        const fresh = await source.record(vesselId, 'opening', id);
        await editor?.submit('opening', id, fresh.approval.contentHash);
        await load(id);
        setDone(t('fuel.records.submitted'));
      } else if (action === 'correct' && detail && editor) {
        const opened2 = await editor.correct('opening', detail.id, newKey());
        await load(opened2.id);
        setDone(t('fuel.records.correctionOpened', { no: detail.recordNo }));
      }
    } catch (failure) {
      setError(errorText(failure));
      setIssues(issuesOf(failure));
    } finally {
      setBusy(null);
    }
  };

  const actions = workflowActions(status, context?.can ?? EMPTY_CAN, editor !== undefined);
  const people = new Map((context?.crew ?? []).map((p) => [p.id, p.name]));
  const fuelOptions = (context?.fuelTypes ?? [])
    .filter((f) => !tank || tank.allowedFuelTypeIds.includes(f.id))
    .map((f) => ({ value: f.id, label: f.code }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      title={t('fuel.opening.title', { tank: tank ? `${tank.name} (${tank.code})` : '' })}
      closeButtonProps={{ 'aria-label': t('fuel.fuelTypes.close') }}
    >
      <div className={classes.body} style={{ padding: 0 }}>
        <p className={classes.hint}>{t('fuel.opening.hint')}</p>
        {detail ? (
          <div className={classes.picker}>
            <strong>{detail.recordNo}</strong>
            <StatusBadge status={detail.status} />
          </div>
        ) : null}
        <StatusBanner detail={detail} people={people} correctsNo={null} />
        <Notices error={error} done={done} />
        <div className={classes.grid3}>
          <Select
            label={t('fuel.opening.fields.fuel')}
            withAsterisk
            data={fuelOptions}
            value={values.fuelTypeId}
            onChange={(v) => set('fuelTypeId', v)}
            readOnly={!editable}
            error={issue('fuelTypeId')}
          />
          <div className={classes.span2}>
            <DateTime
              label={t('fuel.opening.fields.measured')}
              day={values.day}
              time={values.time}
              onDay={(v) => set('day', v)}
              onTime={(v) => set('time', v)}
              readOnly={!editable}
              error={issue('measuredLocal')}
            />
          </div>
          <Select
            label={t('fuel.opening.fields.method')}
            withAsterisk
            data={METHODS.map((m) => ({ value: m, label: t(`fuel.opening.methods.${m}`) }))}
            value={values.method}
            onChange={(v) => set('method', v)}
            readOnly={!editable}
            error={issue('method')}
          />
          <Amount
            label={t('fuel.opening.fields.volume')}
            value={values.volumeM3}
            onChange={(v) => set('volumeM3', v)}
            readOnly={!editable}
            error={issue('volumeM3')}
          />
          <Amount
            label={t('fuel.opening.fields.density')}
            value={values.density15}
            onChange={(v) => set('density15', v)}
            readOnly={!editable}
            error={issue('density15')}
            required
          />
          <Amount
            label={t('fuel.opening.fields.mass')}
            value={values.massMt}
            onChange={(v) => set('massMt', v)}
            readOnly={!editable}
            error={issue('massMt')}
            required
          />
          <TextInput
            className={classes.span2}
            label={t('fuel.opening.fields.basis')}
            withAsterisk
            readOnly={!editable}
            value={values.basis}
            onChange={(e) => set('basis', e.currentTarget.value)}
            error={issue('basis') ?? issue('tankId')}
          />
          <Textarea
            className={classes.span5}
            style={{ gridColumn: '1 / -1' }}
            label={t('fuel.opening.fields.remarks')}
            readOnly={!editable}
            autosize
            minRows={2}
            maxLength={500}
            value={values.remarks}
            onChange={(e) => set('remarks', e.currentTarget.value)}
          />
        </div>
        {actions.length > 0 ? (
          <div className={classes.footer} style={{ padding: 0 }}>
            <WorkflowButtons
              actions={actions}
              busy={busy as never}
              onAction={(action) => void act(action)}
            />
          </div>
        ) : null}
      </div>
      <DecisionModal
        mode={modal}
        recordNo={detail?.recordNo ?? ''}
        onClose={() => setModal(null)}
        onConfirm={async (decision) => {
          if (!editor || !detail || !modal) return;
          if (modal === 'reverse') {
            await editor.reverse('opening', detail.id, detail.rowVersion, decision.reason);
          } else if (detail.approval.pending) {
            await editor.decide('opening', detail.id, {
              decision: modal === 'approve' ? 'approved' : 'returned',
              reason: modal === 'return' ? decision.reason : null,
              contentHash: detail.approval.contentHash,
              expected: {
                submissionId: detail.approval.pending.submissionId,
                step: detail.approval.pending.step,
              },
              ...(decision.password ? { password: decision.password } : {}),
            });
          }
          await load(detail.id);
          setDone(
            modal === 'approve'
              ? t('fuel.records.approved')
              : modal === 'return'
                ? t('fuel.records.returned')
                : t('fuel.records.reversed'),
          );
        }}
      />
    </Modal>
  );
}
